import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { TicketStatusZendesk } from 'pages/support/pages/Tickets/types/enums/TicketStatus'

/**
 * Adjust this type if you already have a Ticket type in your codebase.
 * Using `any` keeps it drop-in for now.
 */
export type ZendeskTicket = any

type SearchCacheEntry = {
  results: ZendeskTicket[]
  updatedAt: number
}

type TicketsCacheState = {
  // "default list" cache (useGetTickets)
  tickets: ZendeskTicket[]
  ticketsUpdatedAt: number | null

  // search cache: key = normalized search term
  searchByTerm: Record<string, SearchCacheEntry>

  // helpers
  setTickets: (tickets: ZendeskTicket[]) => void
  clearTickets: () => void

  setSearchResults: (term: string, results: ZendeskTicket[]) => void
  getSearchResults: (term: string) => SearchCacheEntry | null
  clearSearch: () => void

  // staleness helpers
  isTicketsStale: (maxAgeMs: number) => boolean
  isSearchStale: (term: string, maxAgeMs: number) => boolean
}

// tiny helper
const normalizeTerm = (term: string) => term.trim().toLowerCase()

export const useTicketsCacheStore = create<TicketsCacheState>()(
  persist(
    (set, get) => ({
      tickets: [],
      ticketsUpdatedAt: null,
      searchByTerm: {},

      setTickets: (tickets) =>
        set({
          tickets,
          ticketsUpdatedAt: Date.now(),
        }),

      clearTickets: () =>
        set({
          tickets: [],
          ticketsUpdatedAt: null,
        }),

      setSearchResults: (term, results) => {
        const key = normalizeTerm(term)
        if (!key) return

        set((state) => ({
          searchByTerm: {
            ...state.searchByTerm,
            [key]: { results, updatedAt: Date.now() },
          },
        }))
      },

      getSearchResults: (term) => {
        const key = normalizeTerm(term)
        if (!key) return null
        return get().searchByTerm[key] ?? null
      },

      clearSearch: () =>
        set({
          searchByTerm: {},
        }),

      isTicketsStale: (maxAgeMs) => {
        const ts = get().ticketsUpdatedAt
        if (!ts) return true
        return Date.now() - ts > maxAgeMs
      },

      isSearchStale: (term, maxAgeMs) => {
        const key = normalizeTerm(term)
        if (!key) return true
        const entry = get().searchByTerm[key]
        if (!entry?.updatedAt) return true
        return Date.now() - entry.updatedAt > maxAgeMs
      },
    }),
    {
      name: 'support_tickets_cache_v1',
      // If you want to avoid persisting big search dictionaries, you can partialize
      // partialize: (state) => ({ tickets: state.tickets, ticketsUpdatedAt: state.ticketsUpdatedAt }),
      version: 1,
    }
  )
)

// Optional: shared helpers for filtering
export const filterTicketsByTab = (
  tickets: ZendeskTicket[],
  activeTab: TicketStatusZendesk
) => {
  if (activeTab === TicketStatusZendesk.OPEN) {
    return tickets.filter((t) => t.status === TicketStatusZendesk.OPEN)
  }
  if (activeTab === TicketStatusZendesk.SOLVED) {
    return tickets.filter((t) => t.status === TicketStatusZendesk.SOLVED)
  }
  if (activeTab === TicketStatusZendesk.CLOSED) {
    return tickets.filter((t) => t.status === TicketStatusZendesk.CLOSED)
  }
  return tickets
}
